import { useNavigate, useParams } from 'react-router-dom'

import { Add, Delete, File, Pencil, Person, Upload } from '@factorialco/f0-react/icons/app'
import { TrainingParticipantsQueryVariables } from 'generated/resources/graphql'
import {
  TrainingDetailFragment,
  TrainingParticipantsDocument,
  TrainingParticipantsQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { TRAINING_MEMBERSHIP_STATUS } from 'modules/trainings/hooks/useTrainingsDatatable'

export type TrainingsTrainingMembershipEdge =
  TrainingParticipantsQuery['trainings']['trainingMembershipsConnection']['edges'][number]
export type TrainingsTrainingMembership =
  TrainingParticipantsQuery['trainings']['trainingMembershipsConnection']['edges'][number]['node']

// Export the mapped variables type for use in parent components
export type TrainingParticipantsMappedVariables = Omit<
  TrainingParticipantsQueryVariables,
  'page' | 'perPage' | 'cursor'
>

export const useDataSource = (
  training: TrainingDetailFragment,
  trainingClasses: readonly { id: string; name: string | null }[],
  onOpenExportCertificatesModal: () => void,
  options?: { bulkImportEnabled?: boolean }
) => {
  const { id } = useParams()
  const { filters: employeeFilters } = useEmployeeFilters()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const trainingClassesPath = (accessId: number) => {
    if (trainingClasses.length !== 0) {
      return navigation.trainings.list.details_revamp.view.participants.assignParticipant.fullPath({
        id: Number(id),
        accessId: accessId,
      })
    }

    return navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath({
      id: Number(id),
    })
  }

  return useGraphqlDataCollectionSource({
    document: TrainingParticipantsDocument,
    mapData: (data) =>
      data.trainings.trainingMembershipsConnection.edges.map(
        (edge: TrainingsTrainingMembershipEdge) => edge.node
      ),
    mapVariables: ({ filters, search, sortings }) => {
      const filterObject: Record<string, unknown> = {}

      if (filters.trainingGroup && filters.trainingGroup.length > 0) {
        filterObject.classId = { in: filters.trainingGroup }
      }

      if (filters.courseStatus && filters.courseStatus.length > 0) {
        filterObject.status = { in: filters.courseStatus }
      }

      if (filters.team && filters.team.length > 0) {
        filterObject.teamId = { in: filters.team }
      }

      if (filters.dueDate && filters.dueDate.length > 0) {
        filterObject.dueDate = { in: filters.dueDate }
      }

      return {
        trainingId: Number(id),
        includeComputedFields: true,
        filter: filterObject,
        sortOrder: sortings?.[0],
        search,
      }
    },
    perPage: 100,
    paginationType: 'pages',
    mapPagination: (data) => {
      const connection = data.trainings.trainingMembershipsConnection
      return {
        total: connection.totalCount ?? connection.edges.length,
        hasNextPage: connection.pageInfo.hasNextPage ?? false,
        cursor: connection.pageInfo.endCursor ?? undefined,
      }
    },
    sortings: {
      fullName: {
        label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.reviewed_name'),
      },
      status: {
        label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.reviewed_status'),
      },
      trainingDueDate: {
        label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.due_date.title'),
      },
      trainingCompletedAt: {
        label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.completion_date'),
      },
    },
    defaultSortings: {
      field: 'fullName' as const,
      order: 'asc',
    },
    itemUrl: (item: TrainingsTrainingMembership) =>
      navigation.trainings.list.details_revamp.view.participants.employees.fullPath({
        id: training.id,
        employeeId: item.employee?.id?.toString() || '', // Hacky but needed until we make employeeId non nilable
      }),
    primaryActions: () => ({
      label: i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.dropdown.add_participants'
      ),
      onClick: () => {
        navigate(
          navigation.trainings.list.details_revamp.view.participants.addParticipants.fullPath({
            id: Number(id),
          })
        )
      },
      icon: Add,
    }),
    secondaryActions: () => {
      return [
        {
          label: i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.export_certificates'
          ),
          icon: Upload,
          onClick: onOpenExportCertificatesModal,
        },
      ]
    },
    bulkActions: (item) => {
      const isSelectAll = item.allSelected === true || item.allSelected === 'indeterminate'

      const deleteLabel = isSelectAll
        ? i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.dropdown.delete_all_participants'
          )
        : item.selectedIds.length > 1
          ? i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.dropdown.delete_participants'
            )
          : i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.dropdown.delete_participant'
            )

      const assignLabel = isSelectAll
        ? i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.dropdown.assign_all_participants'
          )
        : i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.dropdown.assign_participants'
          )

      const exportCertificatesLabel = isSelectAll
        ? i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.export_all_certificates'
          )
        : i18n.t('trainings.trainings_revamp.training_detail.employees_table.export_certificates')

      const importCertificatesLabel = isSelectAll
        ? i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.import_all_certificates'
          )
        : i18n.t('trainings.trainings_revamp.training_detail.employees_table.import_certificates')

      const secondaryActions = [
        {
          label: assignLabel,
          id: 'assign-to-group',
        },
        {
          label: exportCertificatesLabel,
          id: 'export-certificates',
        },
        ...(options?.bulkImportEnabled
          ? [
              {
                label: importCertificatesLabel,
                id: 'import-certificates',
              },
            ]
          : []),
      ]

      return {
        primary: [
          {
            label: deleteLabel,
            id: 'delete-all',
          },
        ],
        secondary: secondaryActions,
      }
    },
    selectable: (item) => item.employee?.id,
    itemActions: (item: TrainingsTrainingMembership) => [
      {
        label: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.dropdown.assign_participants'
        ),
        onClick: () => {
          navigate(trainingClassesPath(item.access.id))
        },
        icon: Person,
      },
      {
        label: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.update_completed_date_modal.title'
        ),
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.participants.updateCompletionDate.fullPath(
              { id: Number(id), membershipId: item.id }
            )
          )
        },
        icon: Pencil,
        enabled: item.status === TRAINING_MEMBERSHIP_STATUS.COMPLETED,
      },
      {
        label: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.dropdown.delete_participant'
        ),
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.participants.deleteParticipant.fullPath({
              id: Number(id),
              membershipId: item.id,
            })
          )
        },
        icon: Delete,
      },
      ...(item.trainingMembershipFiles?.length > 0 && item.employee
        ? [
            {
              label: i18n.t(
                'trainings.trainings_revamp.training_detail.employees_table.dropdown.view_certificate'
              ),
              onClick: () => {
                const file = item.trainingMembershipFiles[0]
                if (file && item.employee) {
                  track(trackingEvents.training.trainingWorkflows.goToCertificateDatatable)
                  navigate(
                    navigation.trainings.list.details_revamp.view.participants.previewCertificate.fullPath(
                      {
                        id: training.id,
                        employeeId: item.employee.id,
                        trainingMembershipId: file.trainingMembershipId,
                        trainingMembershipFileId: file.id,
                      }
                    )
                  )
                }
              },
              icon: File,
            },
          ]
        : []),
    ],
    search: {
      enabled: true,
    },
    filters: {
      trainingGroup: {
        label: i18n.t('trainings.trainings_revamp.training_employee_table.filters.class_id'),
        type: 'in' as const,
        options: {
          options: trainingClasses.map((traingingClass) => {
            return {
              label: traingingClass.name || '',
              value: Number(traingingClass.id),
            }
          }),
        },
      },
      courseStatus: {
        label: i18n.t('trainings.trainings_revamp.training_employee_table.filters.status'),
        type: 'in' as const,
        options: {
          options: [
            {
              label: i18n.t('trainings.my_training.all_courses_table.not_assigned'),
              value: TRAINING_MEMBERSHIP_STATUS.NOT_ASSIGNED,
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.not_started'),
              value: TRAINING_MEMBERSHIP_STATUS.NOT_STARTED,
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.completed'),
              value: TRAINING_MEMBERSHIP_STATUS.COMPLETED,
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.started'),
              value: TRAINING_MEMBERSHIP_STATUS.STARTED,
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.partially_completed'),
              value: TRAINING_MEMBERSHIP_STATUS.PARTIALLY_COMPLETED,
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.absent'),
              value: TRAINING_MEMBERSHIP_STATUS.MISSING,
            },
          ],
        },
      },
      ...(training.validFor
        ? {
            dueDate: {
              label: i18n.t('trainings.trainings_revamp.training_employee_table.filters.due_date'),
              type: 'in' as const,
              options: {
                options: [
                  {
                    label: i18n.t(
                      'trainings.trainings_revamp.training_detail.employees_table.due_date.overdue'
                    ),
                    value: 'overdue',
                  },
                  {
                    label: i18n.t(
                      'trainings.trainings_revamp.training_employee_table.due_date_options.due_in_3_months'
                    ),
                    value: '90',
                  },
                  {
                    label: i18n.t(
                      'trainings.trainings_revamp.training_employee_table.due_date_options.no_due_date'
                    ),
                    value: 'no_due_date',
                  },
                ],
              },
            },
          }
        : {}),
      team: employeeFilters.team,
    },
    presets: [
      {
        label: i18n.t('trainings.my_training.all_courses_table.not_assigned'),
        filter: {
          courseStatus: [TRAINING_MEMBERSHIP_STATUS.NOT_ASSIGNED],
        },
      },
      ...(training.validFor
        ? [
            {
              label: i18n.t(
                'trainings.trainings_revamp.training_detail.employees_table.due_date.overdue'
              ),
              filter: {
                dueDate: ['overdue'],
              },
            },
          ]
        : []),
    ],
  })
}
